/**
 * File: useBooksInvoicesQueries.ts
 * Path: apps/admin/src/shared/hooks/useBooksInvoicesQueries.ts
 * Description: The invoices the books hold for brought-in contracts. A link
 * under each contract says which books entry a payment was billed on; the
 * number, date and amount are read from that entry, so nothing is copied.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { COMPANY_PROFILE_ID } from "@idk/config";
import { accountingRepo } from "@/shared/services/accountingService";
import { contractsRepo } from "../services/contractsService";
import { contractsKey } from "./useContractsQueries";

/**
 * An invoice that lives only in the books (the Zoho history), found through
 * the link saved on its contract's payment. Its figures are the books' own.
 */
export interface BooksInvoiceView {
  contractId: string;
  /** The payment reference it is linked to. */
  reference: string;
  number: string;
  /** The books entry's number, to open it. */
  journalNumber: number;
  issuedOn: string;
  totalCents: number;
}

const linksKey = [...contractsKey, "invoiceLinks"] as const;

export const useBooksInvoices = (enabled = true) => {
  const links = useQuery({ queryKey: linksKey, queryFn: contractsRepo.allInvoiceLinks, enabled });
  const ids = Object.values(links.data ?? {})
    .flat()
    .map((l) => l.journalId)
    .sort();
  const journals = useQuery({
    queryKey: [...linksKey, "journals", ids.join(",")],
    queryFn: () => accountingRepo.getJournals(COMPANY_PROFILE_ID, ids),
    enabled: enabled && links.data !== undefined && ids.length > 0,
  });

  const rows = useMemo<BooksInvoiceView[]>(() => {
    const byId = new Map((journals.data ?? []).map((j) => [j.id, j]));
    return Object.entries(links.data ?? {}).flatMap(([contractId, list]) =>
      list.flatMap((link) => {
        const journal = byId.get(link.journalId);
        return journal
          ? [
              {
                contractId,
                reference: link.reference,
                number: link.number,
                journalNumber: journal.number,
                issuedOn: journal.date,
                totalCents: journal.totalCents,
              },
            ]
          : [];
      }),
    );
  }, [links.data, journals.data]);
  return {
    rows,
    isLoading: links.isLoading || (ids.length > 0 && journals.isLoading),
    error: links.error ?? journals.error,
    refetch: () => {
      void links.refetch();
    },
  };
};

export const booksInvoicesByPayment = (rows: readonly BooksInvoiceView[]): Map<string, BooksInvoiceView> =>
  new Map(rows.map((r) => [r.reference, r]));

export { linksKey as invoiceLinksKey };
